import type { ResumeWorkflowOptions, ResumeWorkflowResult } from "./workflow-runner.js";
import { resumeWorkflowRun } from "./workflow-runner.js";
import { fireDueWorkflowV2Timers } from "./workflow-kernel-v2-durable.js";
import { collectWorkflowV2ErrorCodes, collectWorkflowV2ErrorMessages } from "./workflow-kernel-v2-db.js";
import { resumeWorkflowV2Run, type ResumeWorkflowV2Result } from "./workflow-kernel-v2-runner.js";
import { getWorkflowV2Run } from "./workflow-kernel-v2-store.js";

function isMissingWorkflowV2SchemaError(error: unknown): boolean {
  const codes = new Set(collectWorkflowV2ErrorCodes(error));
  const message = collectWorkflowV2ErrorMessages(error).join("\n");
  return (
    codes.has("42P01") ||
    (codes.has("42703") && message.includes("_gencow_workflow")) ||
    message.includes('relation "_gencow_workflow_runs_v2" does not exist') ||
    message.includes("relation _gencow_workflow_runs_v2 does not exist") ||
    message.includes('relation "_gencow_workflow_timers_v2" does not exist') ||
    message.includes("relation _gencow_workflow_timers_v2 does not exist")
  );
}

async function getOptionalWorkflowV2Run(options: ResumeWorkflowOptions): Promise<boolean> {
  try {
    await fireDueWorkflowV2Timers(options.db);
    return (await getWorkflowV2Run(options.db, options.workflowId)) != null;
  } catch (error) {
    if (isMissingWorkflowV2SchemaError(error)) return false;
    throw error;
  }
}

function scheduleWorkflowV2Followup(options: ResumeWorkflowOptions, result: ResumeWorkflowV2Result): void {
  if (result.status === "sleeping") {
    const wakeAtMs = new Date(result.wakeAt).getTime();
    const delayMs = Number.isFinite(wakeAtMs) ? Math.max(0, wakeAtMs - Date.now()) : 0;
    options.scheduleResume(result.runId, delayMs);
    return;
  }
  if (result.status === "waiting" && result.wakeAt) {
    const wakeAtMs = new Date(result.wakeAt).getTime();
    const delayMs = Number.isFinite(wakeAtMs) ? Math.max(0, wakeAtMs - Date.now()) : 0;
    options.scheduleResume(result.runId, delayMs);
    return;
  }
  if (result.status === "requeued") {
    options.scheduleResume(result.runId, result.nextDelayMs);
    return;
  }
  if (result.status === "capacity_deferred") {
    options.scheduleResume(result.runId, result.nextDelayMs);
  }
}

export async function resumeWorkflowRunWithV2(
  options: ResumeWorkflowOptions,
): Promise<ResumeWorkflowResult | ResumeWorkflowV2Result> {
  const hasV2Run = await getOptionalWorkflowV2Run(options);
  if (!hasV2Run) return resumeWorkflowRun(options);

  const result = await resumeWorkflowV2Run({
    db: options.db,
    runId: options.workflowId,
    workerId: `workflow-v2:${options.workflowId}`,
    capacityPolicy: options.capacityPolicy,
    buildCtx: options.buildCtx,
  });
  scheduleWorkflowV2Followup(options, result);
  return result;
}
